import React, { ChangeEvent, FocusEvent, KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Timeout } from '../../types/timeout'
import { localeCodeToLocaleIdentifier } from '../../utils/localeCodeToLocaleIdentifier'
import { SHAKE_ANIMATION_TIME_MS } from '../../utils/shakeAnimation'
import { DATE_PICKER_WIDTH, DATE_PICKER_YEAR_DROPDOWN_ID, DatePicker, DatePickerProps } from '../DatePicker'
import { Dropdown, DropdownProps, DropdownRef } from '../Dropdown'
import { Icon } from '../Icon'
import { InputProps, InputRef } from '../Input'
import { DATE_INPUT_DROPDOWN_ID } from './constants/dateInputDropdownId'
import { SHAKE_CLASS_NAME } from './constants/shakeClassName'
import * as Styled from './styles'
import { getDisplayDate } from './utils/getDisplayDate'
import { getParsedDateFromString } from './utils/getParsedDateFromString'
import { isValidDate } from './utils/isValidDate'

const DATE_PICKER_PREVIEW_DATE_SIGNS_LIMIT = 6

export interface DateInputProps
  extends Pick<DatePickerProps, 'firstDayOfWeek' | 'isDateBlocked' | 'unavailableDates'>,
    Pick<
      InputProps,
      | 'alignment'
      | 'allowClear'
      | 'autoCompleted'
      | 'autoFocus'
      | 'bordered'
      | 'className'
      | 'disabled'
      | 'error'
      | 'focused'
      | 'hidden'
      | 'id'
      | 'name'
      | 'onBlur'
      | 'onClear'
      | 'onPressEnter'
      | 'placeholder'
      | 'prefix'
      | 'selectOnFocus'
      | 'size'
      | 'success'
    >,
    Pick<DatePickerProps, 'locale' | 'dayWrapperRender'> {
  datePickerId?: DropdownProps['id']
  datePickerPlacement?: DropdownProps['placement']
  defaultValue?: Date | null
  isOpen?: boolean
  maxDate?: Date | null
  minDate?: Date | null
  onChange?: (date?: Date | null) => void
  onClose?: () => void
  onOpen?: () => void
  shakeOnNotValid?: boolean
  showIcon?: boolean
  value?: Date | null
}

export const DateInput = ({
  // DateInput props
  defaultValue,
  dayWrapperRender,
  onChange,
  shakeOnNotValid = true,
  locale,
  // DatePicker props
  firstDayOfWeek,
  isDateBlocked,
  maxDate,
  minDate,
  unavailableDates,
  // Input props
  alignment,
  allowClear,
  autoCompleted,
  autoFocus,
  bordered,
  className,
  disabled,
  error,
  focused,
  hidden,
  id,
  name,
  onBlur,
  onClear,
  onPressEnter,
  placeholder,
  prefix,
  selectOnFocus = true,
  showIcon = true,
  size,
  success,
  // Dropdown props
  datePickerId,
  datePickerPlacement,
  isOpen: isOpenControlled = false,
  onClose,
  onOpen,
  ...props
}: DateInputProps) => {
  const { value } = props
  const isControlled = 'value' in props
  const localeIdentifier = localeCodeToLocaleIdentifier(locale.code)

  const { t } = useTranslation()

  const [inputValue, setInputValue] = useState(getDisplayDate(value || defaultValue, localeIdentifier) || '')
  const [isOpen, setIsOpen] = useState(isOpenControlled)

  // helper for not clearing data and on blur, after selecting some item (check handleInputBlur).
  // Also fix for clicking on the edge in the input to not close it just after clicking
  const isDateClickedRef = useRef(false)
  const isDropdownClosedManually = useRef(false)
  const dropdownRef = useRef<DropdownRef<InputRef>>(null)
  const shakeTimeoutRef = useRef<Timeout>()
  const lastValidValueRef = useRef<Date | undefined | null>(value)

  const parsedDate = useMemo(() => getParsedDateFromString(inputValue), [inputValue])

  const isValid = useMemo(() => {
    return isValidDate(parsedDate, { isDateBlocked, unavailableDates, minDate, maxDate })
  }, [parsedDate, isDateBlocked, unavailableDates, minDate, maxDate])

  const previewDateValue = useMemo(() => {
    if (inputValue.length < DATE_PICKER_PREVIEW_DATE_SIGNS_LIMIT) {
      return undefined
    }

    return isValid ? parsedDate : undefined
  }, [inputValue.length, isValid, parsedDate])

  // Helper functions

  const setInputValueFormatted = useCallback(
    (date?: Date | null) => {
      setInputValue(getDisplayDate(date, localeIdentifier))
    },
    [localeIdentifier],
  )

  // Mutations

  useEffect(() => {
    if (isControlled) {
      setInputValue(getDisplayDate(value, localeIdentifier))
    }

    lastValidValueRef.current = value
  }, [isControlled, localeIdentifier, value])

  useEffect(() => {
    return () => {
      if (shakeTimeoutRef.current) {
        clearTimeout(shakeTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    setIsOpen(isOpenControlled)
  }, [isOpenControlled])

  // Functions

  const shakeInput = useCallback(() => {
    dropdownRef.current?.trigger?.wrapper?.classList.add(SHAKE_CLASS_NAME)

    shakeTimeoutRef.current = setTimeout(() => {
      dropdownRef.current?.trigger?.wrapper?.classList.remove(SHAKE_CLASS_NAME)
    }, SHAKE_ANIMATION_TIME_MS)
  }, [])

  const openDropdown = useCallback(() => {
    setIsOpen(true)
  }, [])

  const closeDropdown = useCallback(() => {
    isDropdownClosedManually.current = true // optimization to not call 'handleDropdownClose' redundantly
    setIsOpen(false)
  }, [])

  const toggleDropdown = useCallback(() => {
    setIsOpen((prevIsOpen) => !prevIsOpen)
  }, [])

  const changeDate = useCallback(
    (date?: Date | null) => {
      onChange?.(date)
      setInputValueFormatted(date)
    },
    [onChange, setInputValueFormatted],
  )

  const setDateFromInputValue = useCallback(
    (inputValue: string) => {
      if (isValid) {
        changeDate(parsedDate)
      } else {
        const fallbackDate = inputValue ? lastValidValueRef.current : null
        changeDate(fallbackDate)

        if (shakeOnNotValid || error) {
          shakeInput()
        }
      }
    },
    [changeDate, error, isValid, parsedDate, shakeInput, shakeOnNotValid],
  )

  // Handlers

  const handleInputClear = useCallback(() => {
    changeDate(null)
    onClear?.()
  }, [changeDate, onClear])

  const handleInputChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target
    setInputValue(value)
  }, [])

  const handleDatePickerChange = useCallback(
    (date: Date | null) => {
      if (date) {
        changeDate(date)
      }

      closeDropdown()
    },
    [changeDate, closeDropdown],
  )

  const handleInputEnterPress = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!isValid && lastValidValueRef?.current && inputValue) {
        setDateFromInputValue?.(getDisplayDate(lastValidValueRef.current, localeIdentifier))
      } else {
        setDateFromInputValue?.(inputValue)
      }

      onPressEnter?.(event)
      closeDropdown()
    },
    [closeDropdown, inputValue, isValid, localeIdentifier, onPressEnter, setDateFromInputValue],
  )

  const handleInputMouseDown = useCallback(() => {
    if (!disabled) {
      toggleDropdown()
    }
  }, [toggleDropdown, disabled])

  const handleDropdownMouseDown = useCallback(() => {
    isDateClickedRef.current = true
  }, [])

  const handleInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      if (isDateClickedRef.current) {
        /* If blur was caused by interacting with the calendar, skip the blur validation (setDateFromInputValue) to avoid overwriting
        We don't need to validate the input in this case, it is our value from calendar */
        isDateClickedRef.current = false
        onBlur?.(event)
        return
      }

      closeDropdown()
      setDateFromInputValue(event.target.value)
      onBlur?.(event)
    },
    [closeDropdown, onBlur, setDateFromInputValue],
  )

  const handleDropdownOpen = useCallback(() => {
    openDropdown()
    onOpen?.()
  }, [openDropdown, onOpen])

  const handleDropdownClose = useCallback(() => {
    if (isDropdownClosedManually.current) {
      isDropdownClosedManually.current = false
    }

    setDateFromInputValue?.(inputValue)
    closeDropdown()
    onClose?.()
  }, [closeDropdown, onClose, setDateFromInputValue, inputValue])

  return (
    <Dropdown
      id={datePickerId || DATE_INPUT_DROPDOWN_ID}
      childrenIds={[DATE_PICKER_YEAR_DROPDOWN_ID]}
      autoToggle={false}
      isOpen={isOpen}
      onMouseDown={handleDropdownMouseDown}
      onClose={handleDropdownClose}
      onOpen={handleDropdownOpen}
      trigger={
        <Styled.Input
          alignment={alignment}
          allowClear={allowClear}
          autoComplete="off"
          autoCompleted={autoCompleted}
          autoFocus={autoFocus}
          bordered={bordered}
          className={className}
          disabled={disabled}
          error={error}
          focused={focused}
          hidden={hidden}
          id={id}
          name={name}
          onBlur={handleInputBlur}
          onChange={handleInputChange}
          onClear={handleInputClear}
          onMouseDown={handleInputMouseDown}
          onPressEnter={handleInputEnterPress}
          placeholder={placeholder !== undefined ? placeholder : t('ds.date_input.placeholder')}
          prefix={prefix}
          selectOnFocus={selectOnFocus}
          size={size}
          success={success}
          suffix={showIcon ? <Icon icon="calendar" /> : undefined}
          value={inputValue}
        />
      }
      placement={datePickerPlacement || 'bottom-start'}
      ref={dropdownRef}
      width={DATE_PICKER_WIDTH}
    >
      <DatePicker
        dayWrapperRender={dayWrapperRender}
        firstDayOfWeek={firstDayOfWeek}
        isDateBlocked={isDateBlocked}
        // DatePicker (external dependencies) requires Date | undefined. We explicitly convert null to undefined
        // to prevent type errors in the underlying library.
        maxDate={maxDate || undefined}
        minDate={minDate || undefined}
        onChange={handleDatePickerChange}
        unavailableDates={unavailableDates}
        value={previewDateValue}
        locale={locale}
      />
    </Dropdown>
  )
}
